import React from 'react';
import { Icon } from 'react-icons-kit';
import { ic_check } from 'react-icons-kit/md/ic_check';
import { ic_priority_high } from 'react-icons-kit/md/ic_priority_high';

export type AlertStatus = 'error' | 'warning' | 'success';

// The spec sheet's alert (allenai/design.md, spec-sheet.html, EarthRanger
// theme): a white box with a light line and 8px corners, a 4px stripe down the
// left in the status colour, and a round 20px icon in the same colour. Class
// names are spelled out in full so Tailwind can see them.
const STATUS: Record<
  AlertStatus,
  { stripe: string; dot: string; icon: typeof ic_check }
> = {
  error: {
    stripe: 'border-l-ui-status-error-destructive',
    dot: 'bg-ui-status-error-destructive text-white',
    icon: ic_priority_high,
  },
  warning: {
    stripe: 'border-l-ui-status-warning',
    dot: 'bg-ui-status-warning text-teal-ai2-extra-dark-teal',
    icon: ic_priority_high,
  },
  success: {
    stripe: 'border-l-ui-status-success',
    dot: 'bg-ui-status-success text-white',
    icon: ic_check,
  },
};

const Alert = ({
  status,
  action,
  className = '',
  testId,
  children,
}: {
  status: AlertStatus;
  // A button on the right, such as Retry or Enable.
  action?: React.ReactNode;
  className?: string;
  testId?: string;
  children: React.ReactNode;
}) => {
  const { stripe, dot, icon } = STATUS[status];
  return (
    <div
      className={`grid grid-cols-[20px_1fr_auto] items-center gap-3 rounded-lg border border-l-4 border-ui-divider-lines bg-white px-3.5 py-3 text-body-sm text-ui-text-primary-text ${stripe} ${className}`}
      data-testid={testId}
    >
      <span
        aria-hidden="true"
        className={`flex size-5 items-center justify-center rounded-full ${dot}`}
      >
        <Icon icon={icon} size={14} style={{ display: 'flex' }} />
      </span>
      <div className="min-w-0">{children}</div>
      {action ? <div>{action}</div> : <span />}
    </div>
  );
};

export default Alert;
